import Controller from '@ember/controller';
import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { initializeIconElement } from '@databox/icons/dist/custom/initializers/icon-element';
import type { ProfileAnchor } from '@databox/types/domains/settings/settings.enums';
import { SettingsTab } from '@databox/types/domains/settings/settings.enums';

import type { EventData, Service } from 'webapp-types';
import { SupportChannel } from 'webapp/enums/ai-chat';
import { InfolayerItemType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { uniq } from 'webapp/utils/common';

import DataManagerPopupSelectedConnectionsComponent from 'webapp/components/data-manager/popup-selected-connections';
import type GoalsStrategyController from 'webapp/controllers/goals/strategy';

import { Type as GeneralType } from 'webapp/models-custom/infolayer-item/general';

import { Severity } from 'webapp/services/logger';
import { Step } from 'webapp/services/onboarding';

export default class ApplicationController extends Controller {
	@service('store') declare storeService: Service.Store;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('logger') declare loggerService: Service.Logger;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('router') declare routerService: Service.Router;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('popup') declare popupService: Service.Popup;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('wizard') declare wizardService: Service.Wizard;
	@service('metric-raw-data') declare metricRawDataService: Service.MetricRawData;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;

	override queryParams = ['customize', 'anchor'];

	@tracked customize: SettingsTab | null = null;
	// The Profile anchor the dialog opens on; dropped once shown.
	@tracked anchor: ProfileAnchor | null = null;

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);

		this.setupDebug();

		this.navigationService.triggerEventForEmailCampaign();
		initializeIconElement();

		// bind events
		$(window).on('resize.narrow-view', () => {
			debounce(this.navigationMainService, this.navigationMainService.checkNarrowView, 100);
		});

		this.navigationMainService.runInitialChecks();
	}

	@action
	onCustomizeChange(tab: SettingsTab): void {
		this.aiChatService.openSettings(tab, tab === SettingsTab.PROFILE ? this.anchor : null);
	}

	@action
	onAnchorReached(): void {
		this.aiChatService.openSettings(SettingsTab.PROFILE);
	}

	get hasNavigationMain() {
		return !this.skipNavigationMain && (!this.isWideWrapper || this.isGoalStrategyTreeView);
	}

	get hasExpandedNavigationMain() {
		return this.navigationMainService.isNavigationMainExpanded;
	}

	get skipNavigationMain() {
		return this.navigationMainService.skipNavigationMain;
	}

	get skipNavigationSection() {
		if (this.skipNavigationMain) {
			return true;
		}
		return (
			!this.navigationService.initialRequestsDone ||
			this.navigationService.isOnboarding ||
			this.navigationService.isHome ||
			this.navigationService.isDesigner ||
			this.navigationService.isReportEditorSubpage ||
			this.navigationService.isGenie ||
			this.navigationService.isActivatedRouteGenieShared ||
			this.navigationService.isActivatedRouteSharedArtifact ||
			this.navigationService.isArtifacts ||
			this.navigationService.isRoutines ||
			this.navigationService.isActivatedRouteCompanyMetrics ||
			this.navigationService.isComingSoonPages
		);
	}

	get isVerticallyStretchedPage() {
		// for pages that embed react directly and need to be 100vh
		return (
			this.navigationService.isGenie ||
			this.navigationService.isRoutines ||
			this.navigationService.isActivatedRouteCompanyMetrics ||
			this.navigationService.isComingSoonPages ||
			(this.navigationService.isOnboarding &&
				this.onboardingService.currentStep === Step.GENIE_EXP) ||
			this.navigationService.isHome
		);
	}

	get isGoalStrategyTreeView() {
		const controller = this.navigationService.appRoute.controllerFor(
			'goals/strategy',
		) as GoalsStrategyController;
		return (
			this.navigationService.activatedRoute.startsWith('goals.strategy') &&
			controller.viewId === 'grid'
		);
	}

	get isWideWrapper() {
		return (
			!this.navigationService.isMobileApp &&
			(this.skipNavigationMain || this.isGoalStrategyTreeView)
		);
	}

	get wrapperClass() {
		const classes: string[] = [];

		if (this.isWideWrapper) {
			classes.push('wide');
		}

		if (this.hasNavigationMain) {
			classes.push('has-navigation-main');

			if (this.navigationMainService.isNarrowView) {
				classes.push('has-narrow-navigation-main');
			} else if (this.hasExpandedNavigationMain) {
				classes.push('has-expanded-navigation-main');
			}
		}

		if (this.skipNavigationSection) {
			classes.push('no-navigation-section');
			classes.push('no-side-paddings');
		}

		if (this.isVerticallyStretchedPage) {
			classes.push('vertical-stretch');
		}

		if (this.showAppNotifications) {
			classes.push('has-notification');
		}

		// add all popup classes
		const popupClasses: string[] = [];
		const popupClassPrefix = 'active-popup';
		this.popupService.popupsActive.forEach((popup) => {
			popupClasses.push(`${popupClassPrefix}--${popup.isContext ? 'context' : 'modal'}`);
			if (popup.isModal && popup.hasOverlay) {
				popupClasses.push(`${popupClassPrefix}--modal-with-overlay`);
			}
			popup.popupClass.split(' ').forEach((c) => {
				popupClasses.push(`${popupClassPrefix}--${c}`);
			});
		});

		classes.push(...uniq(popupClasses));

		return classes.join(' ');
	}

	get showAppNotifications() {
		return (
			this.appNotificationsService.showAppNotifications &&
			!this.skipNavigationMain &&
			!this.navigationService.isOnboarding &&
			!this.navigationService.isWizard &&
			!this.navigationService.isDesigner &&
			!this.navigationService.isMobileApp
		);
	}

	get showOutlet() {
		return !this.navigationService.isE2EApp;
	}

	get isNpsFeedbackFormShown() {
		return (
			this.currentUserService.isEligibleForNpsFormToBeShown &&
			this.navigationService.initialRequestsDone &&
			this.navigationService.activatedSection === 'home'
		);
	}

	get shouldLoadSourceConnect() {
		return this.navigationService.isMainApp;
	}

	get isLoggedInApp(): boolean {
		const { navigationService } = this;
		return (
			(navigationService.isMainApp || navigationService.isMobileApp) &&
			!navigationService.isActivatedRouteGenieShared &&
			!navigationService.isActivatedRouteSharedArtifact
		);
	}

	// only in the logged-in app: shared pages must not open settings from a url param
	get genieSettingsTab(): SettingsTab | null {
		return this.isLoggedInApp ? this.customize : null;
	}

	// the explorer navigates into main-app routes, so shared pages and the datawall don't get it
	get shouldMountGenieLineageExplorer(): boolean {
		return this.isLoggedInApp && this.aiChatService.isAiChatAvailable;
	}

	get shouldShowGeniePanel() {
		return this.aiChatService.hasBeenOpened;
	}

	// Only where Genie is the account's support chat (free and trial plans get Intercom's launcher instead), and
	// in-app pages only; the Genie page and a routine run are a chat themselves, and onboarding (the pre-app flow)
	// must not be left through the panel's "Open in Genie"
	get shouldShowGenieLauncher() {
		return (
			this.aiChatService.supportChannel === SupportChannel.GENIE &&
			this.hasNavigationMain &&
			!this.navigationService.isGenieChatPage &&
			!this.navigationService.isOnboarding
		);
	}

	setupDebug() {
		// this is for debugging purposes
		// don't allocate when testing so the app can be cleanup-ed
		const app = getOwner(this)!;

		window.debug = (item: string) => {
			let fullString: `${string}:${string}`;
			if (!item.includes(':')) {
				// if type not specified, type is service
				fullString = `service:${item}`;
			} else {
				fullString = item as typeof fullString;
			}
			return app.lookup(fullString);
		};

		if (
			window.location.host === 'local.databox.com' ||
			window.location.host === 'localhost:10000'
		) {
			window.LOGIT = true;
		}
	}

	@action
	onIsExpandedChange(isExpanded: boolean) {
		this.navigationMainService.isNavigationMainExpanded = isExpanded;
	}

	@action
	sourceConnected(source: EventData.ByName[AppEvent.SOURCE_CONNECTED], isReconnect = false) {
		const isActive = source.statusObject && source.statusObject?.level === 'active',
			sourceType =
				source.type !== 0
					? this.mmSourceTypesService.findModelById(source.type)
					: this.mmSourceTypesService.findModelByKey(source.key),
			datasource = sourceType?.name || source.title,
			isCurrentUserOnboarded = this.currentUserService.isOnboarded;

		this.storeService.pushPayload({ source });

		// Workflow integration is returned without status object and cannot be not active
		if (isActive || sourceType?.isWithCustomToken) {
			if (isCurrentUserOnboarded) {
				this.infolayerService.createNew(InfolayerItemType.GENERAL, {
					id: `source${source.id}`,
					type: GeneralType.OK,
					text: `<strong>${source.title}</strong> connected!`,
				});
			}
		} else {
			this.loggerService.logError(
				'Connect ended in error',
				{
					sourceId: source.id,
					key: datasource,
					status: JSON.stringify(source.statusObject),
				},
				Severity.INFO,
			);
		}

		if (isReconnect && sourceType?.isFileUpload) {
			this.analyticsService.event(TrackingEvent.FILE_REUPLOADED, { datasource });
		}

		if (!isCurrentUserOnboarded) {
			this.analyticsService.event(TrackingEvent.ONBOARDING_CONNECT_SUCCESS, { datasource });
		}

		const formattedDatasource =
			this.onboardingService.excelDemoSourceFileName === source.title
				? 'Excel Demo'
				: datasource;

		let origin;
		if (this.flowTourOnboardingService.isBannerShown) {
			origin = 'guided_onboarding';
		} else if (
			this.popupService.popupStack
				.map((item) => item.componentInnerClass)
				.includes(DataManagerPopupSelectedConnectionsComponent)
		) {
			origin = 'connect_selected_integrations_popup';
		} else if (!isCurrentUserOnboarded) {
			origin = 'onboarding';
		}

		if (sourceType?.isToken) {
			this.analyticsService.event(TrackingEvent.NEW_TOKEN_CREATED);
		} else {
			this.analyticsService.event(TrackingEvent.NEW_CONNECTION_ADDED, {
				datasource: formattedDatasource,
				origin,
			});
		}

		this.eventManagerService.trigger(AppEvent.SOURCE_CONNECTED, source);
	}
}
